import { Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useI18n } from "@/i18n";
import type { Language } from "@/i18n";
import { cn } from "@/lib/cn";

interface LangOption {
  code: Language;
  labelKey: string;
  flag: string;
}

const LANGS: LangOption[] = [
  { code: "uz", labelKey: "common.languageUz", flag: "🇺🇿" },
  { code: "ru", labelKey: "common.languageRu", flag: "🇷🇺" },
  { code: "en", labelKey: "common.languageEn", flag: "🇬🇧" },
];

export function LanguageToggle() {
  const { language, setLanguage, t } = useI18n();
  const current = LANGS.find((l) => l.code === language) ?? LANGS[0]!;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("common.language")}
          className={cn(
            "inline-flex h-8 items-center gap-1.5 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-2.5 text-xs",
            "text-[var(--text-secondary)] transition-colors hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-violet)]/60",
          )}
        >
          <Globe className="h-3.5 w-3.5" aria-hidden />
          <span className="font-mono uppercase tracking-wider">{current.code}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8}>
        {LANGS.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onSelect={() => setLanguage(lang.code)}
            className={cn(language === lang.code && "bg-[var(--bg-elevated)]")}
          >
            <span className="text-base leading-none" aria-hidden>
              {lang.flag}
            </span>
            <span>{t(lang.labelKey)}</span>
            {language === lang.code && (
              <span className="ml-auto text-[var(--accent-violet)]">●</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
